<template>
  <header class="app-page-header">
    <div class="app-page-header-left">
      <slot name="left">
        <h2 v-if="title">{{ title }}</h2>
        <p v-if="subtitle">{{ subtitle }}</p>
      </slot>

      <div v-if="$slots.meta" class="app-page-header-meta">
        <slot name="meta" />
      </div>
    </div>

    <div class="app-page-header-right">
      <slot name="right" />

      <slot name="actions" :actions="normalizedActions" :on-action="emitAction">
        <div v-if="normalizedActions.length" class="app-page-header-actions">
          <div
            v-for="(action, index) in normalizedActions"
            :key="action.key || index"
            class="app-page-header-action-wrap"
          >
            <button
              class="app-header-btn"
              :class="[
                `app-header-btn--${action.variant || 'primary'}`,
                action.className || '',
                action.iconOnly ? 'app-header-btn--icon-only' : '',
              ]"
              type="button"
              :title="action.title || action.label"
              :disabled="action.disabled || action.loading"
              @click="handleActionClick(action, index)"
            >
              <i
                v-if="action.loading"
                class="fa fa-circle-o-notch fa-spin"
                aria-hidden="true"
              ></i>
              <i
                v-else-if="action.icon"
                class="fa"
                :class="action.icon"
                aria-hidden="true"
              ></i>
              <span v-if="!action.iconOnly">{{ action.label }}</span>
            </button>

            <div
              v-if="hasMenuItems(action) && openActionMenuKey === (action.key || `index-${index}`)"
              class="app-table-actions-menu app-page-header-action-menu"
            >
              <button
                v-for="(item, itemIndex) in action.items"
                :key="item.key || itemIndex"
                type="button"
                @click="emitMenuAction(action, item, index, itemIndex)"
              >
                <i v-if="item.icon" class="fa" :class="item.icon" aria-hidden="true"></i>
                <span>{{ item.label }}</span>
              </button>
            </div>
          </div>
        </div>
      </slot>
    </div>
  </header>
</template>

<script>
export default {
  name: 'PageHeader',
  emits: ['action'],
  props: {
    title: {
      type: String,
      default: ''
    },
    subtitle: {
      type: String,
      default: ''
    },
    actions: {
      type: Array,
      default: () => []
    },
    buttonLabel: {
      type: String,
      default: ''
    },
    buttonIcon: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      openActionMenuKey: '',
    };
  },
  mounted() {
    document.addEventListener('click', this.handleDocumentClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleDocumentClick);
  },
  computed: {
    normalizedActions() {
      const declarativeActions = Array.isArray(this.actions) ? this.actions : [];
      const legacyAction = this.buttonLabel
        ? [{
            key: 'legacy-primary-action',
            label: this.buttonLabel,
            icon: this.buttonIcon,
            variant: 'primary'
          }]
        : [];

      return [...declarativeActions, ...legacyAction].filter((action) => {
        return action && typeof action === 'object' && String(action.label || '').trim() !== '';
      });
    }
  },
  methods: {
    hasMenuItems(action) {
      return Array.isArray(action?.items) && action.items.length > 0;
    },
    resolveActionKey(action, index) {
      return String(action?.key || `index-${index}`);
    },
    handleActionClick(action, index) {
      if (this.hasMenuItems(action)) {
        const key = this.resolveActionKey(action, index);
        this.openActionMenuKey = this.openActionMenuKey === key ? '' : key;
        return;
      }

      this.emitAction(action, index);
    },
    emitMenuAction(parentAction, itemAction, parentIndex, itemIndex) {
      this.openActionMenuKey = '';
      const item = itemAction && typeof itemAction === 'object' ? itemAction : {};
      this.$emit('action', {
        ...item,
        parentKey: parentAction?.key || '',
        menuIndex: itemIndex,
      }, parentIndex);
    },
    handleDocumentClick(event) {
      const target = event?.target;
      if (!target || !(target instanceof Element)) {
        return;
      }

      if (target.closest('.app-page-header-action-wrap')) {
        return;
      }

      this.openActionMenuKey = '';
    },
    emitAction(action, index) {
      this.$emit('action', action, index);
    }
  }
};
</script>

<style scoped>
.app-page-header-action-wrap {
  position: relative;
}

.app-page-header-action-menu {
  min-width: 220px;
  right: 0;
  left: auto;
  top: calc(100% + 6px);
}

.app-page-header-action-menu button {
  display: flex;
  align-items: center;
  gap: 8px;
}

.app-header-btn--icon-only {
  width: 38px;
  min-width: 38px;
  height: 38px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.app-header-btn--sync {
  background: #e6fffb;
  border-color: #0f766e;
  color: #0f766e;
}

.app-header-btn--sync:hover:not(:disabled) {
  background: #ccfbf1;
  border-color: #0f766e;
  color: #115e59;
}

.app-header-btn--export {
  background: #eef2ff;
  border-color: #4f46e5;
  color: #4338ca;
}

.app-header-btn--export:hover:not(:disabled) {
  background: #e0e7ff;
  border-color: #4338ca;
  color: #312e81;
}

.app-header-btn--download-list {
  background: #ecfeff;
  border-color: #0891b2;
  color: #0e7490;
}

.app-header-btn--download-list:hover:not(:disabled) {
  background: #cffafe;
  border-color: #0e7490;
  color: #155e75;
}
</style>
